import { FullscreenOutlined, RotateRightOutlined } from '@ant-design/icons'
import { Button, Modal, Spin, Typography } from 'antd'
import { ExcelCsvViewer } from 'features/files/components/ExcelCsvViewer'
import { ImageViewer } from 'features/files/components/ImageViewer'
import { PdfViewer, PdfViewerHandle } from 'features/files/components/PdfViewer'
import { useDocumentSignedUrls } from 'features/files/hooks/useDocuments'
import { RolodexDocumentEntry } from 'features/files/types'
import { SmartImageViewer } from 'features/libra'
import { ExternalLink } from 'lucide-react'
import * as React from 'react'
import styled from 'styled-components'

const { Text } = Typography

export interface DocumentPreviewHandle {
  scrollToPage: (page: number) => void
}

export interface DocumentPreviewProps {
  document: RolodexDocumentEntry | null
  height?: number
  className?: string
  containerStyle?: React.CSSProperties
  continuousScroll?: boolean
  virtualize?: boolean
  initialPage?: number
  title?: string
  onPageChange?: (page: number) => void
  onOpenInNewTab?: () => void
  onRotatePage?: (page: number, newRotation: number) => Promise<void>
  onRotateAll?: (angle: number, numPages: number) => Promise<void>
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const TitleContainer = styled.div`
  flex: 1;
`

const UnsupportedState = styled.div`
  text-align: center;
  padding: 1.25rem;
  color: #999;
`

const FullscreenPdfViewer = styled(PdfViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const FullscreenImageViewer = styled(ImageViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const FullscreenExcelCsvViewer = styled(ExcelCsvViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const ModalTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PageIndicator = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
`

const LoadingContainer = styled.div`
  text-align: center;
  padding: 1.25rem;
`

interface FullscreenModalProps {
  document: RolodexDocumentEntry
  signedUrl: string
  visible: boolean
  onClose: () => void
  isPdf: boolean
  isImage: boolean
  isExcelCsv: boolean
  continuousScroll?: boolean
  virtualize?: boolean
  initialPage?: number
  pageRotations?: Record<string, number>
}

const FullscreenModal: React.FC<FullscreenModalProps> = ({
  document,
  signedUrl,
  visible,
  onClose,
  isPdf,
  isImage,
  isExcelCsv,
  continuousScroll = false,
  virtualize = false,
  initialPage,
  pageRotations,
}) => (
  <Modal
    open={visible}
    onCancel={onClose}
    footer={null}
    destroyOnClose
    width='95vw'
    style={{ top: '1.25rem' }}
    styles={{
      body: {
        padding: 0,
        height: '90vh',
      },
    }}
    closable={true}
    title={
      <ModalTitleContainer>
        <span>
          {document.filename || document.visible_name || 'Unknown file'}
        </span>
      </ModalTitleContainer>
    }>
    {isPdf && (
      <FullscreenPdfViewer
        url={signedUrl}
        continuousScroll={continuousScroll}
        virtualize={virtualize}
        initialPage={initialPage}
        pageRotations={pageRotations}
      />
    )}
    {isImage && <FullscreenImageViewer url={signedUrl} height={1000} />}
    {isExcelCsv && (
      <FullscreenExcelCsvViewer
        url={signedUrl}
        fileType={document.file_type || ''}
      />
    )}
  </Modal>
)

export const DocumentPreview = React.forwardRef<
  DocumentPreviewHandle,
  DocumentPreviewProps
>(function DocumentPreview(
  {
    document,
    height = 400,
    className,
    containerStyle,
    continuousScroll = false,
    virtualize = false,
    initialPage,
    title,
    onPageChange,
    onOpenInNewTab,
    onRotatePage,
    onRotateAll,
  },
  imperativeRef
) {
  const { signedUrl, isLoading, isPdf, isImage, isExcelCsv } =
    useDocumentSignedUrls(document)
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const [isRotating, setIsRotating] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(initialPage ?? 1)
  const [numPages, setNumPages] = React.useState<number | null>(null)
  const pdfRef = React.useRef<PdfViewerHandle>(null)

  React.useImperativeHandle(imperativeRef, () => ({
    scrollToPage: (page: number) => {
      pdfRef.current?.scrollToPage(page)
    },
  }))

  const pageRotations = document?.pageRotations
  const currentPageRotation = pageRotations?.[String(currentPage)] ?? 0

  const handlePageChange = React.useCallback(
    (page: number) => {
      setCurrentPage(page)
      onPageChange?.(page)
    },
    [onPageChange]
  )

  const handleRotatePage = React.useCallback(async () => {
    if (!onRotatePage) return
    setIsRotating(true)
    try {
      await onRotatePage(currentPage, (currentPageRotation + 90) % 360)
    } finally {
      setIsRotating(false)
    }
  }, [onRotatePage, currentPage, currentPageRotation])

  const handleRotateAll = React.useCallback(async () => {
    if (!onRotateAll || !numPages) return
    setIsRotating(true)
    try {
      await onRotateAll(90, numPages)
    } finally {
      setIsRotating(false)
    }
  }, [onRotateAll, numPages])

  if (!document) {
    return (
      <Container className={className} style={containerStyle}>
        <Text
          strong
          style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
          Document Preview
        </Text>
        <UnsupportedState>
          <Text type='secondary'>Select a document to preview</Text>
        </UnsupportedState>
      </Container>
    )
  }

  const canPreview = isPdf || isImage || isExcelCsv
  const previewTitle = title
    ? title
    : isPdf
      ? 'PDF Preview'
      : isImage
        ? 'Image Preview'
        : isExcelCsv
          ? 'Spreadsheet Preview'
          : 'File Preview'

  return (
    <>
      <Container className={className} style={containerStyle}>
        <HeaderContainer>
          <TitleContainer>
            <Text
              strong
              style={{ fontSize: 16, display: 'block' }}
              ellipsis={{ tooltip: previewTitle }}>
              {previewTitle}
            </Text>
          </TitleContainer>
          {canPreview && signedUrl && (
            <>
              {onRotatePage && isPdf && (
                <>
                  <PageIndicator>p.{currentPage}</PageIndicator>
                  <Button
                    type='text'
                    icon={<RotateRightOutlined />}
                    onClick={handleRotatePage}
                    loading={isRotating}
                    title='Rotate page'
                  />
                </>
              )}
              {onRotateAll && isPdf && (
                <Button
                  type='text'
                  onClick={handleRotateAll}
                  loading={isRotating}
                  title='Rotate all pages'>
                  All <RotateRightOutlined />
                </Button>
              )}
              {onOpenInNewTab && (
                <Button
                  type='text'
                  icon={<ExternalLink size={14} />}
                  onClick={onOpenInNewTab}
                  title='Open in new tab'
                />
              )}
              <Button
                type='text'
                icon={<FullscreenOutlined />}
                onClick={() => setIsFullscreen(true)}
                title='Open in fullscreen'
              />
            </>
          )}
        </HeaderContainer>

        <ContentArea>
          {isPdf ? (
            signedUrl ? (
              <PdfViewer
                ref={pdfRef}
                url={signedUrl}
                height={height}
                continuousScroll={continuousScroll}
                showPagination={!continuousScroll}
                initialPage={initialPage}
                onPageChange={handlePageChange}
                onLoadSuccess={setNumPages}
                pageRotations={pageRotations}
              />
            ) : isLoading ? (
              <LoadingContainer>
                <Spin />
              </LoadingContainer>
            ) : (
              <UnsupportedState>
                <Text type='secondary'>Unable to load PDF preview</Text>
              </UnsupportedState>
            )
          ) : isImage ? (
            signedUrl && document ? (
              <SmartImageViewer
                url={signedUrl}
                documentId={document.entry_id}
                height={height}
                enableZoneDrawing={false}
              />
            ) : isLoading ? (
              <LoadingContainer>
                <Spin tip='Loading image preview...' />
              </LoadingContainer>
            ) : (
              <UnsupportedState>
                <Text type='secondary'>Unable to load image preview</Text>
              </UnsupportedState>
            )
          ) : isExcelCsv ? (
            signedUrl ? (
              <ExcelCsvViewer
                url={signedUrl}
                fileType={document.file_type || ''}
              />
            ) : isLoading ? (
              <LoadingContainer>
                <Spin tip='Loading spreadsheet preview...' />
              </LoadingContainer>
            ) : (
              <UnsupportedState>
                <Text type='secondary'>Unable to load spreadsheet preview</Text>
              </UnsupportedState>
            )
          ) : (
            <UnsupportedState>
              <Text type='secondary'>
                Preview not available for this file type
              </Text>
            </UnsupportedState>
          )}
        </ContentArea>
      </Container>

      {document && signedUrl && canPreview && (
        <FullscreenModal
          document={document}
          signedUrl={signedUrl}
          visible={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          isPdf={Boolean(isPdf)}
          isImage={Boolean(isImage)}
          isExcelCsv={Boolean(isExcelCsv)}
          continuousScroll={continuousScroll}
          virtualize={virtualize}
          initialPage={initialPage}
          pageRotations={pageRotations}
        />
      )}
    </>
  )
})
